import React, { useState, RefObject, useEffect } from 'react';
import classNames from 'classnames';
import searchLoader from '../../images/loader_search.gif';
import styles from './TextArea.module.css';

export interface TextAreaProps {
  id?: string;
  /**
   * Boolean to display black X clear button
   */
  hasClearButton?: boolean;
  shouldFocus?: boolean;

  /**
   * Can set the loading indicator outside the scope of clicking on it
   */
  isLoading?: boolean;

  /**
   * Optional callback for when the black X button is clicked
   */
  onClearClick?: () => Promise<void> | void;

  /**
   * Optional onFocus when the user clicks on textarea field
   */
  onFocus?: () => Promise<void> | void;

  /**
   * Optional callback for when user types into text field
   */
  onTextAreaChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;

  /**
   * Optional placeholder text
   */
  placeholder?: string;
  /*
   * Value of the textarea
   */
  value?: string;
  customCSS?: string;
  textAreaRef?: RefObject<HTMLTextAreaElement>;
  lengthLimit?: number;
  dataTestId?: string;
  disabled?: boolean;
  rows?: number;
}

export const TextArea: React.FC<TextAreaProps> = ({
  dataTestId,
  hasClearButton = false,
  isLoading = false,
  onClearClick,
  onFocus,
  shouldFocus = false,
  onTextAreaChange,
  placeholder = '',
  value = '',
  customCSS = '',
  textAreaRef,
  lengthLimit,
  disabled = false,
  id,
  rows = 3,
}) => {
  const [isClearing, setIsClearing] = useState(false);
  const handleClearButton = async () => {
    setIsClearing(true);
    onClearClick && (await onClearClick());
    setIsClearing(false);
  };

  useEffect(() => {
    if (shouldFocus && textAreaRef?.current) {
      textAreaRef.current.focus();
    }
  }, [shouldFocus, textAreaRef]);

  return (
    <div className={classNames(styles.textareaRoot, customCSS)}>
      <textarea
        id={id}
        className={classNames(styles.textarea, hasClearButton && styles.hasClearButton)}
        placeholder={placeholder}
        onChange={onTextAreaChange}
        value={value}
        ref={textAreaRef}
        maxLength={lengthLimit}
        data-test-id={dataTestId}
        disabled={disabled}
        onFocus={onFocus}
        rows={rows}
      />
      {hasClearButton &&
        (isLoading || isClearing ? (
          <div
            className={classNames(styles.buttonContainer, styles.loadingContainer)}
            data-test-id="cancelSearch"
          >
            <img src={searchLoader} className={styles.loadingImage} alt="" />
          </div>
        ) : (
          <div
            className={classNames(styles.clearButton, styles.buttonContainer)}
            onClick={handleClearButton}
            data-test-id="cancelSearch"
          />
        ))}
    </div>
  );
};
